import {
  ReactNode,
  useEffect,
  useState,
} from 'react';
import {
  Editor,
  EditorContent,
  useEditor,
  JSONContent,
  EditorEvents,
  generateJSON,
} from '@tiptap/react';
import { DragHandle } from '@tiptap/extension-drag-handle-react';
import { Extension, getHTMLFromFragment } from '@tiptap/core';
import { ResolvedPos } from '@tiptap/pm/model';
import { Plugin, TextSelection } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import Link from '@tiptap/extension-link';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import Image from '@tiptap/extension-image';
import Mention from '@tiptap/extension-mention';
import { Placeholder } from '@tiptap/extensions';
import {
  Color,
  FontSize,
  TextStyle,
} from '@tiptap/extension-text-style';
import { HardBreakNode, InvisibleCharacters } from '@tiptap/extension-invisible-characters';

import { classNames } from '../../../../common/lib/classNames';
import { suggestion } from '../utils/suggestion';

import { sanitizeFontFamily } from './fontFamilies';
import { IToolbarAction, MenuBar } from './TextEditorToolbar';
import {
  DynamicSectionNode,
  IDynamicSection,
  SectionTag,
  expandSections,
  sectionTagText,
  splitSections,
} from './dynamicSections';
import { EmailButtonNode } from './emailButton';
import { SenderBlockNode } from './senderBlock';
import { EditorIcon } from './EditorIcon';
import { ISubjectLineProps, SubjectLine } from './SubjectLine';

//  Styles for inside the editor
import '../../../css/editor.css';

const starterJson = {
  'type': `doc`,
  'content': [
    {
      'type': `paragraph`,
    },
  ],
};

const fontFamilyExtension = Extension.create({
  name: `fontFamily`,
  addGlobalAttributes() {
    return [
      {
        types: [`textStyle`],
        attributes: {
          fontFamily: {
            default: null,
            parseHTML: element => sanitizeFontFamily(element.style.fontFamily),
            renderHTML: attributes => {
              const safeFontFamily = sanitizeFontFamily(attributes.fontFamily);

              if (!safeFontFamily) {
                return {};
              }

              return {
                style: `font-family: ${safeFontFamily}`,
              };
            },
          },
        },
      },
    ];
  },
});

// Draws {{{MergeTag}}} text as a pill. Decoration only, the saved content is unchanged
export const mergeTagHighlightExtension = Extension.create({
  name: `mergeTagHighlight`,
  addProseMirrorPlugins() {
    return [
      new Plugin({
        props: {
          decorations: ({ doc }) => {
            const decorations: Decoration[] = [];

            doc.descendants((node, pos) => {
              if (!node.text) return;

              for (const match of node.text.matchAll(/\{\{\{[^}]+\}\}\}/g)) {
                const from = pos + match.index;

                decorations.push(Decoration.inline(from, from + match[0].length, { class: `merge-tag` }));
              }
            });

            return DecorationSet.create(doc, decorations);
          },
        },
      }),
    ];
  },
});

// Every {{{MergeTag}}} in the textblock around $pos. Non-text leaves count as one character so offsets match positions
const mergeTagsAround = ($pos: ResolvedPos) => {
  if (!$pos.parent.inlineContent) return [];

  const text = $pos.parent.textBetween(0, $pos.parent.content.size, `\0`, `\0`);

  return [...text.matchAll(/\{\{\{[^}]+\}\}\}/g)].map(m => ({ from: $pos.start() + m.index, to: $pos.start() + m.index + m[0].length }));
};

const tagContaining = ($pos: ResolvedPos) => mergeTagsAround($pos).find(t => t.from < $pos.pos && $pos.pos < t.to);

// Makes merge tags behave as one character, so they can't be half deleted or typed into.
// A broken tag like {{{PaymentLink}} fails to render
export const mergeTagAtomExtension = Extension.create({
  name: `mergeTagAtom`,
  addProseMirrorPlugins() {
    return [
      new Plugin({
        // Moves selection ends that land inside a tag to its edge: the way the cursor was going, else the nearest
        appendTransaction: (transactions, oldState, { doc, selection, tr }) => {
          if (!(selection instanceof TextSelection)) return null;

          const oldHead = transactions.reduce((pos, t) => t.mapping.map(pos), oldState.selection.head);
          const headTag = tagContaining(selection.$head);
          let head = selection.head;

          if (headTag) {
            const nearest = head - headTag.from < headTag.to - head ? headTag.from : headTag.to;

            head = head > oldHead ? headTag.to : head < oldHead ? headTag.from : nearest;
          }

          const anchorTag = selection.empty ? null : tagContaining(selection.$anchor);
          const anchor = anchorTag ? (selection.anchor < head ? anchorTag.from : anchorTag.to) : selection.empty ? head : selection.anchor;

          if (head === selection.head && anchor === selection.anchor) return null;

          return tr.setSelection(TextSelection.create(doc, anchor, head));
        },
        props: {
          // With the cursor next to a tag, deletes all of it
          handleKeyDown: (view, event) => {
            const { selection } = view.state;

            if (event.key !== `Backspace` && event.key !== `Delete` || !selection.empty) return false;

            const tag = mergeTagsAround(selection.$head).find(t => (event.key === `Backspace` ? t.to : t.from) === selection.head);

            if (!tag) return false;

            view.dispatch(view.state.tr.delete(tag.from, tag.to));

            return true;
          },
        },
      }),
    ];
  },
});

// Empty paragraphs collapse to nothing in email clients, so blank lines are
// sent with a break inside them. Done on the output rather than on keypress so
// it holds however the blank line was made (Enter, deleting text, paste)
export const fillEmptyParagraphs = (html: string) => html.replace(/<p([^>]*)><\/p>/g, `<p$1><br></p>`);

const autoBreakModes = [`email`, `secondaryContent`, `signature`];

// Images used to be block nodes, so older saved signatures have them directly
// under the doc. Images are inline now (so they can sit side by side), which
// needs them inside a paragraph. Some profiles have the string "null" saved
export const wrapBlockImages = (json: JSONContent | null): JSONContent => ({
  ...json,
  content: json?.content?.map(node => node.type === `image` ? { type: `paragraph`, content: [node] } : node),
});

interface ITextEditorProps {
  mergeTags?: {
    text: string;
    value: string;
    new?: boolean;
    block?: boolean;
    // Only offered with the cursor inside this section
    section?: SectionTag;
  }[];
  onChange: (obj: {
    json: JSONContent;
    html: string;
    text: string;
    // Content of each section in the body, keyed by tag. Only set when sections are passed
    sections?: Partial<Record<SectionTag, { json: JSONContent; html: string }>>;
  }) => void;
  mode?: `email` | `sms` | `signature` | `note` | `secondaryContent`;
  value: string | JSONContent;
  htmlValue?: string;
  editorClassname?: string;
  mentionValues?: string[];
  placeholder?: string;
  // Shown inline, in place of their tag. Only for the email mode
  sections?: IDynamicSection[];
  lockSections?: boolean;
  // Shown above the toolbar. Insert field goes into whichever of subject and body was focused last
  subject?: ISubjectLineProps;
  // Shown under Insert field
  toolbarAction?: IToolbarAction;
  // Shown at the end of the formatting buttons
  toolbarExtra?: ReactNode;
  // Drawn in place of the {{{Logo}}} block so it can be resized. The editor restarts when it changes
  logoUrl?: string | null;
}

export const TextEditor = ({
  mergeTags,
  onChange,
  mode = `email`,
  value,
  htmlValue,
  editorClassname,
  mentionValues = [],
  placeholder,
  sections,
  lockSections = false,
  subject,
  toolbarAction,
  toolbarExtra,
  logoUrl,
}: ITextEditorProps) => {
  const [subjectEditor, setSubjectEditor] = useState<Editor | null>(null);
  const [subjectFocused, setSubjectFocused] = useState(false);

  const onUpdate: (props: EditorEvents[`update`]) => void = ({ editor }) => {
    const text = editor.getText();

    if (!sections) {
      const json = editor.getJSON();
      const html = autoBreakModes.includes(mode) ? fillEmptyParagraphs(editor.getHTML()) : editor.getHTML();

      onChange({ json, html, text });

      return;
    }

    const toHtml = (json: JSONContent) => fillEmptyParagraphs(getHTMLFromFragment(editor.schema.nodeFromJSON(json).content, editor.schema));
    const split = splitSections(editor.getJSON());

    onChange({
      json: split.doc,
      html: toHtml(split.doc),
      text,
      sections: Object.fromEntries(Object.entries(split.sections).map(([tag, json]) => [tag, { json, html: toHtml(json) }])),
    });
  };

  const linkExtension = Link.configure({
    openOnClick: false,
    HTMLAttributes: {
      class: `text-indigo-600 hover:text-indigo-500 underline`,
    },
  });

  const textAlignExtension = TextAlign.configure({
    types: [`heading`, `paragraph`, `doc`, `senderBlock`],
    alignments: [`left`, `center`, `right`, `justify`],
  });

  const imageExtension = Image.configure({
    allowBase64: false,
    inline: true,
    HTMLAttributes: {
      class: `h-auto`,
    },
  });

  const mentionExtention = Mention.configure({
    HTMLAttributes: {
      class: `bg-indigo-100 text-indigo-800 rounded-md p-1`,
    },
    suggestion: suggestion(mentionValues),
    renderText({ node }) {
      return `${node.attrs.mentionSuggestionChar ?? `@`}${node.attrs.label ?? node.attrs.id}`;
    },
  });

  const placeholderExtension = Placeholder.configure({
    placeholder,
  });

  // Only line breaks are marked, space and paragraph marks add too much noise
  const invisibleCharactersExtension = InvisibleCharacters.configure({
    visible: false,
    builders: [new HardBreakNode()],
  });

  const colorExtension = Color.configure({
    types: [`textStyle`],
  });

  // const fileHandlerExtension = FileHandler.configure({
  //   allowedMimeTypes: [
  //     `image/jpeg`,
  //     `image/png`,
  //     `image/gif`,
  //   ],
  //   onPaste: (currentEditor, files, htmlContent) => {
  //     files.forEach(file => {
  //       if (htmlContent) {
  //         // if there is htmlContent, stop manual insertion & let other extensions handle insertion via inputRule
  //         // you could extract the pasted file from this url string and upload it to a server for example
  //         console.log(htmlContent); // eslint-disable-line no-console

  //         return false;
  //       }

  //       const fileReader = new FileReader();

  //       fileReader.readAsDataURL(file);

  //       fileReader.onload = () => {
  //         currentEditor.chain().insertContentAt(currentEditor.state.selection.anchor, {
  //           type: `image`,
  //           attrs: {
  //             src: fileReader.result,
  //           },
  //         }).focus().run();
  //       };
  //     });
  //   },
  // });

  const extensions = [
    // v3 StarterKit bundles link and dropcursor; link is disabled in favour of
    // the separately configured extension below
    StarterKit.configure({
      link: false,
      dropcursor: {
        color: `#E5E7EB`,
        width: 1,
      },
    }),
    colorExtension,
    TextStyle,
    fontFamilyExtension,
    FontSize,
    linkExtension,
    textAlignExtension,
    invisibleCharactersExtension,
    mergeTagAtomExtension,
    mode === `email` || mode === `secondaryContent` ? mergeTagHighlightExtension : null,
    mode === `email` ? EmailButtonNode : null,
    mode === `email` ? SenderBlockNode.configure({ logoUrl }) : null,
    mode === `signature` ? imageExtension : null,
    mode === `note` ? mentionExtention : null,
    placeholder ? placeholderExtension : null,
    sections ? DynamicSectionNode.configure({ sections, locked: lockSections }) : null,
    // mode === `signature` ? fileHandlerExtension : null,
  ].filter(f => f);

  const editor = useEditor({
    onUpdate,
    onFocus: () => setSubjectFocused(false),
    extensions,
    // v3 defaults to false; the toolbar relies on re-rendering per transaction
    // to reflect active marks and selection state
    shouldRerenderOnTransaction: true,
    content: value || starterJson,
    editorProps: {
      transformPastedHTML: html => {
        return html;
      },
      attributes: {
        class: classNames(
          `editor-class bg-white p-4 border border-gray-300 hover:border-gray-400 focus:border-gray-400 rounded-b-md`,
          mode === `sms` ? `min-h-[150px]` : `min-h-[350px]`,
          mode === `signature` && `min-h-[150px]`,
          mode === `note` && `min-h-[100px]`,
          mode === `secondaryContent` && `min-h-[150px]`,
          editorClassname,
        ),
      },
    },
  }, [logoUrl]);

  useEffect(() => {
    if (!editor) return;

    const expand = (json: JSONContent) => sections ? expandSections(json, sections.map(s => ({
      ...s,
      content: (s.json || (s.html ? generateJSON(s.html, extensions) : null))?.content ?? [{ type: `paragraph` }],
    }))) : json;

    // If json is not present, like from a preset or an AI rewrite, generate it from the html.
    // This emits an update so the parent gets the json back
    if (!value && htmlValue) {
      editor.commands.setContent(expand(generateJSON(htmlValue, extensions)));

      return;
    }

    // When upstream value changes, update the editor
    if (value) {
      const { from, to } = editor.state.selection;
      // Same for a section in the body that only has html, so it can be saved
      const sectionNeedsJson = !lockSections && sections?.some(s => s.enabled && !s.json && s.html && htmlValue?.includes(sectionTagText(s.tag)));

      editor.commands.setContent(expand(value as JSONContent), {
        emitUpdate: !!sectionNeedsJson,
        parseOptions: {
          preserveWhitespace: `full`,
        },
      });

      editor.commands.setTextSelection({ from, to });
    }

    if (!value) {
      editor.commands.setContent(starterJson);
    }
  }, [value, htmlValue, editor, sections]);

  return (
    <div className={ `flex flex-col rounded-md` }>
      { subject ? (
        <SubjectLine
          { ...subject }
          onCreate={ setSubjectEditor }
          onFocus={ () => setSubjectFocused(true) } />
      ) : null }
      <MenuBar editor={ editor }
        mergeTags={ mergeTags }
        mode={ mode }
        sections={ sections }
        lockSections={ lockSections }
        subjectEditor={ subject ? subjectEditor : undefined }
        subjectFocused={ subjectFocused }
        action={ toolbarAction }
        extra={ toolbarExtra } />
      { editor && mode === `email` && !lockSections && (
        <DragHandle editor={ editor }>
          <EditorIcon name={ `grip` }
            size={ 16 }
            className={ `text-gray-400 hover:text-gray-600` } />
        </DragHandle>
      ) }
      <EditorContent
        editor={ editor }
        className={ `relative` } />
    </div>
  );
};

